import { Fragment, useState } from "react";
import { RefreshCw, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);
const pad = (n) => String(n).padStart(2, "0");
export const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? " AM" : " PM"}`;

export function nextSlot(weekday, hour) {
  const d = new Date();
  for (let i = 0; i < 8; i++) {
    const x = new Date(d); x.setDate(d.getDate() + i); x.setHours(hour, 0, 0, 0);
    if ((x.getDay() + 6) % 7 === weekday && x > d) return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(hour)}:00`;
  }
  return "";
}

export function SlotChips({ bt, onPick }) {
  if (!bt?.slots?.length) return null;
  return (
    <div data-testid="composer-suggested-slots"><div className="text-[11px] text-slate-500 mb-1">Suggested times ({bt.source === "general" ? "general guidance, not your data" : "from your data"})</div>
      <div className="flex flex-wrap gap-1">{bt.slots.map((s) => <button key={`${s.weekday}-${s.hour}`} type="button" onClick={() => onPick(nextSlot(s.weekday, s.hour))} title={s.why}
        className="text-[11px] px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10" data-testid={`slot-chip-${s.weekday}-${s.hour}`}>{s.day} {hourLabel(s.hour)}</button>)}</div></div>
  );
}

function Heatmap({ cells }) {
  const map = Object.fromEntries(cells.map((c) => [`${c.weekday}-${c.hour}`, c]));
  const max = Math.max(...cells.map((c) => c.avg_rate), 0.01);
  return (
    <div className="overflow-x-auto" data-testid="best-times-heatmap"><div className="grid gap-px" style={{ gridTemplateColumns: `30px repeat(${DAYS.length}, minmax(0, 1fr))` }}>
      <div />{DAYS.map((d) => <div key={d} className="text-[8px] font-mono text-center text-slate-500">{d.slice(0, 2)}</div>)}
      {HOURS.map((h) => <Fragment key={h}><div className="text-[9px] font-mono text-slate-500 pr-1 text-right">{hourLabel(h)}</div>
        {DAYS.map((_, w) => { const c = map[`${w}-${h}`]; return <div key={w} title={c ? `${c.avg_rate}% · ${c.posts} posts` : "No posts"} className="h-3 rounded-sm"
          style={{ background: c ? `rgba(11,93,54,${0.15 + (0.85 * c.avg_rate) / max})` : "rgba(148,163,184,0.12)" }} />; })}</Fragment>)}
    </div></div>
  );
}

export default function BestTimes({ bt, platform, setPlatform, reload }) {
  const [busy, setBusy] = useState(false);
  const sync = async () => { setBusy(true); try { const r = (await api.post("/calendar/best-times/sync")).data; toast.success(`Synced ${r.synced.facebook} Facebook and ${r.synced.instagram} Instagram posts`); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="lx-card space-y-3" data-testid="best-times-card">
      <div className="flex items-center justify-between gap-2"><div className="font-semibold flex items-center gap-1"><Star className="h-4 w-4 text-amber-500" />Best posting times</div>
        <div className="flex gap-1">{["facebook", "instagram"].map((p) => <button key={p} onClick={() => setPlatform(p)} data-testid={`best-times-${p}`} className={`px-2 py-0.5 rounded-full text-[11px] capitalize border ${platform === p ? "border-emerald-500 text-emerald-600 bg-emerald-500/10" : "border-slate-800 text-slate-400"}`}>{p}</button>)}</div></div>
      {!bt ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
        <div className={`text-[10px] font-mono uppercase ${bt.source === "general" ? "text-amber-700" : "text-emerald-600"}`} data-testid="best-times-source">{bt.label}</div>
        {bt.slots_source === "general" && <p className="text-[10px] font-mono uppercase text-amber-700" data-testid="best-times-slots-general">Not enough posts per time slot yet. Suggested times below are general guidance, not your data.</p>}
        <ol className="space-y-1" data-testid="best-times-slots">{bt.slots.map((s, i) => <li key={`${s.weekday}-${s.hour}`} className="text-xs"><b>{i + 1}. {s.day} {hourLabel(s.hour)}</b><span className="block text-slate-500">{s.why}</span></li>)}</ol>
        {bt.source === "your_data" && <Heatmap cells={bt.heatmap} />}
        {bt.source === "general" && <p className="text-[11px] text-slate-500">Your own best times appear after {bt.min_posts} published {platform} posts with reach data ({bt.posts_analysed} so far).</p>}
        <p className="text-[10px] text-slate-500">{bt.metric}. No AI credits used.</p>
        {bt.meta_ready ? <Button size="sm" variant="secondary" onClick={sync} disabled={busy} data-testid="best-times-sync"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Sync engagement from Meta</Button>
          : <p className="text-[11px] text-amber-700" data-testid="best-times-meta-required">Connect Meta to analyse your own posts (SETUP REQUIRED).</p>}
        {bt.synced_at && <p className="text-[10px] text-slate-500">Last synced {bt.synced_at.slice(0, 16).replace("T", " ")}</p>}
      </>}
    </div>
  );
}
